import { useEffect, useState } from 'react';

/** Opponent / side-seat card size defaults (shared layout tokens). */
export const HAND_CARD_W = 34;
export const HAND_CARD_H = 48;
/** Visible strip per stacked card (pixels). */
export const HAND_STACK_STEP = 9;
/** Full spades hand — used to reserve layout space on the felt. */
export const HAND_SLOT_COUNT = 13;

/**
 * Opponent hands — compact card-fan footprint (less table real estate).
 * `step` is the base inter-card gap; SeatHand adds arc rotation for the fan.
 */
const OPP_DEFAULT = { w: 34, h: 48, step: 9 };
const OPP_LARGE = { w: 48, h: 68, step: 12 };
const OPP_IOS = { w: 26, h: 36, step: 7 };
const OPP_IOS_LANDSCAPE = { w: 22, h: 30, step: 6 };

/**
 * Your hand — larger for readability (viewer south seat only).
 * iOS: ~50% larger than prior mobile sizes so 13 cards nearly span the felt width
 * (span ≈ w + 12*step).
 */
const OWN_DEFAULT = { w: 58, h: 80, step: 18 };
const OWN_LARGE = { w: 96, h: 134, step: 30 };
/* Slightly smaller on phone so Gin (and Spades south) fit without vertical scroll */
const OWN_IOS = { w: 58, h: 80, step: 18 }; /* was 69/96/22 */
const OWN_IOS_LANDSCAPE = { w: 42, h: 58, step: 12 }; /* was 48/68/14 */

const MQL_NARROW = '(max-width: 480px)';
const MQL_LANDSCAPE_MOBILE = '(max-height: 500px) and (max-width: 920px)';
const MQL_WIDE = '(min-width: 481px) and (min-height: 501px)';

export function isNarrowMobileLayout() {
  if (typeof window === 'undefined') return false;
  return window.matchMedia(MQL_LANDSCAPE_MOBILE).matches
    || window.matchMedia(MQL_NARROW).matches;
}

/** @deprecated Prefer isNarrowMobileLayout() for viewport checks */
export function isMobileHandLayout(metrics) {
  return isNarrowMobileLayout() || (metrics?.w != null && metrics.w <= 48);
}

function pickBand() {
  if (typeof window === 'undefined') return 'default';
  if (window.matchMedia(MQL_LANDSCAPE_MOBILE).matches) return 'ios-landscape';
  if (window.matchMedia(MQL_NARROW).matches) return 'ios';
  if (window.matchMedia(MQL_WIDE).matches) return 'large';
  return 'default';
}

const OPP_BY_BAND = {
  default: OPP_DEFAULT,
  large: OPP_LARGE,
  ios: OPP_IOS,
  'ios-landscape': OPP_IOS_LANDSCAPE,
};

const OWN_BY_BAND = {
  default: OWN_DEFAULT,
  large: OWN_LARGE,
  ios: OWN_IOS,
  'ios-landscape': OWN_IOS_LANDSCAPE,
};

/** Opponent / non-viewer hand metrics. */
export function getHandMetrics() {
  return OPP_BY_BAND[pickBand()] || OPP_DEFAULT;
}

/** Viewer's own hand — larger than opponents for readability. */
export function getOwnHandMetrics() {
  return OWN_BY_BAND[pickBand()] || OWN_DEFAULT;
}

export function useHandMetrics() {
  const [metrics, setMetrics] = useState(getHandMetrics);
  useEffect(() => {
    const update = () => setMetrics(getHandMetrics());
    const queries = [MQL_NARROW, MQL_LANDSCAPE_MOBILE, MQL_WIDE].map((q) => window.matchMedia(q));
    queries.forEach((mql) => mql.addEventListener('change', update));
    update();
    return () => {
      queries.forEach((mql) => mql.removeEventListener('change', update));
    };
  }, []);
  return metrics;
}

export function useOwnHandMetrics() {
  const [metrics, setMetrics] = useState(getOwnHandMetrics);
  useEffect(() => {
    const update = () => setMetrics(getOwnHandMetrics());
    const queries = [MQL_NARROW, MQL_LANDSCAPE_MOBILE, MQL_WIDE].map((q) => window.matchMedia(q));
    queries.forEach((mql) => mql.addEventListener('change', update));
    update();
    return () => {
      queries.forEach((mql) => mql.removeEventListener('change', update));
    };
  }, []);
  return metrics;
}

export function handAxisForVisualSeat(visualSeat) {
  return visualSeat === 'east' || visualSeat === 'west' ? 'column' : 'row';
}

/**
 * Fixed-size stacked hand: cards overlap; total span grows minimally per card.
 */
export function handStackSpan(count, {
  cardW = HAND_CARD_W,
  cardH = HAND_CARD_H,
  step = HAND_STACK_STEP,
  axis = 'row',
} = {}) {
  if (count <= 0) return 0;
  const base = axis === 'row' ? cardW : cardH;
  if (count === 1) return base;
  return base + (count - 1) * step;
}

/**
 * Step locked as if `maxSlots` cards fill availableWidth (Spades south = 13, Gin = 11).
 * Fewer cards keep the same step and re-center in the strip.
 * maxStep allows nearly face-up spacing so a full hand can span the felt.
 */
export function lockedSouthStep(availableWidth, cardW, maxSlots = HAND_SLOT_COUNT) {
  const minStep = 8;
  // Allow cards to fan out across the table (was 0.72, which capped spread too early)
  const maxStep = Math.max(cardW * 0.92, 36);
  if (!availableWidth || availableWidth <= cardW || maxSlots <= 1) {
    return Math.min(maxStep, Math.max(minStep, HAND_STACK_STEP));
  }
  const raw = (availableWidth - cardW) / (maxSlots - 1);
  return Math.min(maxStep, Math.max(minStep, raw));
}

/** Gin deal is 10; after draw before discard the strip is 11. */
export const GIN_HAND_SLOTS = 11;
